import React, { FC, HTMLProps, useMemo } from 'react'
import Link from '@shared/ui/Navigation/Link'
import s from './MediaCardCompilation.module.sass'

import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { Rating } from '@shared/ui/Feedback/Rating'
import { Title } from '@shared/ui/Typography/Title'
import { useCurrentSize } from '@shared/lib/helpers/classes'

import IconLike from '@icons/icon-like.svg'
import IconSelectionChevronBig from '@icons/icon-selection-chevron--big.svg'
import IconSelectionChevronMedium from '@icons/icon-selection-chevron--medium.svg'
import { ChevronWithTooltip } from '@shared/ui/Feedback/ChevronWithTooltip'
import { TypeMappingMediaDataOutput } from '@shared/api/middleware/mappingAPI/news/type'
import { PickAnalytic } from '@shared/api/analytics/types/types'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { Favorite } from '@features/Favorite'
import { MediaImg } from '@shared/ui/Media/MediaImg'
import { ImageModel } from '@shared/ui/Media/MediaImg/types/types'
import { isHardcodedChevronLink } from '../chevronLinks'

// D-desktop - M-mobile
// type size = 'D1x1 D2x1 M1x1'
interface Props {
  id: string | number
  title: string
  sizes: string
  img: {
    horizontal: string
    vertical: string
  }
  favorite: {
    category: 'news' | 'selection'
    analytic: {
      data: TypeMappingMediaDataOutput
      other: PickAnalytic<'section_name'>
    }
  }
  promo?: boolean
  hideImage?: boolean
  chevron?: boolean
  link: string
  className?: string
}

const sizesImage = {
  D2x1: { width: 610, height: 297 },
  D1x1: { width: 297, height: 297 },
  M1x1: { width: 261, height: 261 },
} as const

type MediaCardCompilationSize = keyof typeof sizesImage

const MediaCardCompilation: FC<Props & HTMLProps<HTMLDivElement>> = React.forwardRef(
  (
    {
      id,
      sizes,
      favorite,
      className = '',
      title,
      img,
      promo = false,
      chevron = false,
      link,
      hideImage = false,
      ...props
    },
    ref,
  ) => {
    const [size] = useCurrentSize(sizes)
    const showChevron = chevron || isHardcodedChevronLink(link)

    const firstNumbers = parseInt(title)
    const firstNumbersIsTrue = !!firstNumbers && firstNumbers > 2
    const titleFormatted = firstNumbersIsTrue ? title.replace(String(firstNumbers), '') : title

    const getPhotoLink = useMemo((): ImageModel => {
      const url = size === 'D2x1' ? img.horizontal : img.vertical
      const dimensions = sizesImage[size as MediaCardCompilationSize]

      return {
        url,
        width: dimensions.width,
        height: dimensions.height,
      }
    }, [img, size])

    return (
      <BlockBoundary>
        <div data-id={id} className={`${s.card} ${className}`} data-size={size} title={title} {...props} ref={ref}>
          <Link href={link} scroll={false}>
            {promo && <Rating id={s.promo} className={s.promo} mode={'fill'} isPromo sizes={'S'} />}

            {showChevron && size === 'M1x1' && (
              <ChevronWithTooltip icon={IconSelectionChevronMedium} className={s['afisha-selection-chevron--medium']} />
            )}
            {showChevron && size !== 'M1x1' && (
              <ChevronWithTooltip icon={IconSelectionChevronBig} className={s['afisha-selection-chevron--big']} />
            )}

            {!!firstNumbersIsTrue && <span className={s.numbers}>{firstNumbers}</span>}

            <Title tag={'h5'} sizes={'h4'} className={s.title}>
              {titleFormatted}
            </Title>

            <Favorite
              category={favorite.category}
              id={Number(id)}
              analytic={{ data: favorite.analytic.data, other: favorite.analytic.other }}
              className={s.favorite}>
              <IconButton sizes={'S'} mode={'white'} icon={IconLike} />
            </Favorite>

            {!hideImage && (
              <MediaImg
                image={getPhotoLink}
                className={s['image-wrapper']}
                flexible={true}
                dimmed={'gradient'}
                borderRadius={'24px'}
              />
            )}
          </Link>
        </div>
      </BlockBoundary>
    )
  },
)

MediaCardCompilation.displayName = 'MediaCardCompilation'

export default MediaCardCompilation
